import React, { useMemo, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useDrag } from '@use-gesture/react';

const DEFAULT_THRESHOLD = 90;

export default function PullToRefresh({ enabled = true, onRefresh, thresholdPx = DEFAULT_THRESHOLD }) {
  const theme = useTheme();
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const atTop = () => (typeof window === 'undefined' ? true : (window.scrollY || 0) <= 0);

  const bind = useDrag(
    async ({ active, movement: [, my], last, cancel, event }) => {
      if (!enabled) return;
      if (!atTop()) {
        cancel?.();
        return;
      }
      // Ignore gestures starting in the grid or controls.
      const t = event?.target;
      if (t?.closest?.('.MuiDataGrid-root')) {
        cancel?.();
        return;
      }

      const next = Math.max(0, Math.min(my, thresholdPx * 1.6));
      if (active) {
        setDistance(next);
        return;
      }

      if (last) {
        const shouldRefresh = next >= thresholdPx;
        setDistance(0);
        if (!shouldRefresh || refreshing) return;
        try {
          setRefreshing(true);
          await onRefresh?.();
        } finally {
          setRefreshing(false);
        }
      }
    },
    {
      axis: 'y',
      filterTaps: true,
      pointer: { touch: true },
      eventOptions: { passive: true },
    }
  );

  const armed = distance >= thresholdPx;
  const pct = useMemo(() => Math.min(100, Math.round((distance / thresholdPx) * 100)), [distance, thresholdPx]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Small top handle so we don't hijack normal scroll. */}
      <Box
        {...bind()}
        sx={{
          height: 14,
          borderRadius: 999,
          background: alpha(theme.palette.primary.main, 0.18),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700 }}>
          {refreshing ? 'Refreshing…' : armed ? 'Release to refresh' : 'Pull to refresh'}
        </Typography>
      </Box>
      {(distance > 0 || refreshing) && (
        <LinearProgress
          variant={refreshing ? 'indeterminate' : 'determinate'}
          value={refreshing ? undefined : pct}
          sx={{ height: 4, borderRadius: 999, mb: 1.5 }}
        />
      )}
    </Box>
  );
}



